"use client";

import { useEffect, type ReactNode } from "react";
import { LocaleContext } from "./i18n-context";
import { DEFAULT_LOCALE, type Locale } from "./i18n-locale";
import { getLocale, readStoredLocale, seedLocale, setLocale } from "./i18n";

interface LocaleProviderProps {
  /**
   * Locale the SERVER rendered with, resolved from the `ui-locale` cookie in the
   * root layout. `null` means the visitor has no cookie yet, so the server used
   * the default locale and a device-local preference may still need applying.
   */
  initialLocale: Locale | null;
  children: ReactNode;
}

/**
 * Publishes the per-request locale and removes the German flash (backlog C4).
 *
 * Before this, locale was client-only: `lib/i18n.ts` read `localStorage` at
 * module load, `useTranslation`'s server snapshot was hardcoded to `"de"`, and
 * `app/layout.tsx` shipped `lang="de"` with a `suppressHydrationWarning` that
 * admitted the problem. An English user therefore got German SSR markup, a full
 * client re-render after hydration, and a possible *third* pass once
 * `useLocalePreference` resolved.
 *
 * This is the locale twin of the theme mechanism the repo already ships
 * (`lib/theme/theme-store.ts` → `ui-theme` cookie → `.dark` stamped in the root
 * layout): a plain cookie, read server-side, no inline script, CSP-safe.
 *
 * Two things happen here, in this order:
 *  1. **During render, before children:** seed the client store with the exact
 *     locale the server used, so the first client render is byte-identical to
 *     the SSR output — and publish it on the context, which is what
 *     `useTranslation` uses as its server snapshot (the module store is
 *     process-global on the server and would leak across concurrent requests).
 *  2. **After mount:** reconcile a device-local preference that predates the
 *     cookie.
 *
 * Catalogs are fetched strictly on demand — the active one by `seedLocale`, the
 * other by whoever needs it (`LanguageSwitcher` prefetches on hover). Nothing
 * blanket-prefetches the inactive catalog: that would defer the ~160 kB rather
 * than avoid it, which is the whole point of the C1 split.
 */
export function LocaleProvider({ initialLocale, children }: LocaleProviderProps) {
  const serverLocale = initialLocale ?? DEFAULT_LOCALE;
  // Lazy, idempotent store initialisation — the standard React pattern for
  // "initialise an external store from a prop"; a no-op on the server and on
  // every render after the first.
  seedLocale(serverLocale);

  useEffect(() => {
    // Only when the server had no cookie to go on: a locale chosen on this
    // device before the cookie existed still deserves to win, just one render
    // later so the SSR markup is never contradicted mid-hydration.
    if (initialLocale === null) {
      const stored = readStoredLocale();
      if (stored && stored !== getLocale()) setLocale(stored);
    }
  }, [initialLocale]);

  return (
    <LocaleContext.Provider value={serverLocale}>{children}</LocaleContext.Provider>
  );
}
